import { useMemo } from 'react'
import type { JSX } from 'react'

import { ChartSeriesProvider, TimeSeriesChart } from '../charts'
import type { ChartAnnotation } from '../charts'
import type { MetricSeries } from '../MetricChart/types'

import { buildLineChartAriaLabel } from './metricLineChartAriaLabel'
import { toChartSeriesInputs } from './chartSeriesInputs'

interface MetricLineChartProps {
  /** Built chart series for a line view: one or more cumulative (Latest) metrics,
   * each optionally followed by a dashed comparison sibling, plus the benchmark
   * view's dashed cohort-median reference line. */
  metrics: MetricSeries[]
  startDate?: Date
  endDate?: Date
  /** When true, the x-axis shows only the comparison-period dates. */
  comparisonOnly?: boolean
  /** Force all series onto one Y-axis (the benchmark view charts two percentage
   * series that would otherwise split across dual axes). */
  singleAxis?: boolean
  /** Month-only date labels (the benchmark view charts a fixed 12-month window). */
  monthly?: boolean
  height?: number
  /** Accessible chart name; defaults to the primary metric titles + "over time". */
  ariaLabel?: string
  /** Timeline flag markers pinned to the x-axis (e.g. the channel's
   * data-collection start). Forwarded to the underlying {@link TimeSeriesChart}. */
  annotations?: ChartAnnotation[]
  /** Day-aligned x-axis ticks so date labels line up under annotation flags. */
  xAxisTicks?: number[]
}

/**
 * Single-channel metric line chart: one or more metrics over time. A thin domain
 * wrapper that maps the view's `MetricSeries` into the new charts module's context
 * and renders the generic {@link TimeSeriesChart} — cumulative lines with a greyed
 * no-data treatment, dashed comparison overlays, and (benchmark view) a dashed
 * cohort-median reference line. Mirrors how the Posts/Content Impact views wrap
 * their primitives.
 *
 * Unlike the column/composed views this does not bucket wider ranges — lines plot
 * points as-is and the primitive thins x-axis ticks itself.
 *
 * @see ../charts/TimeSeriesChart/TimeSeriesChart
 */
export function MetricLineChart({
  metrics,
  startDate,
  endDate,
  comparisonOnly = false,
  singleAxis = false,
  monthly = false,
  height,
  ariaLabel,
  annotations,
  xAxisTicks,
}: MetricLineChartProps): JSX.Element {
  const series = useMemo(() => toChartSeriesInputs(metrics), [metrics])
  const label = ariaLabel ?? buildLineChartAriaLabel(metrics)

  return (
    <ChartSeriesProvider series={series}>
      <TimeSeriesChart
        ariaLabel={label}
        singleAxis={singleAxis}
        monthly={monthly}
        comparisonOnly={comparisonOnly}
        startDate={startDate}
        endDate={endDate}
        height={height}
        annotations={annotations}
        xAxisTicks={xAxisTicks}
      />
    </ChartSeriesProvider>
  )
}
